import { useEffect, useRef } from 'react';
import { isBackendConfigured } from '@/api/miniapp/client';
import { useAuthStore } from '@/store/authStore';

/**
 * Поддержание сессии, пока мини-приложение открыто.
 *
 * Опрос идёт раз в 25 секунд и только при видимой вкладке: в свёрнутом
 * приложении продлевать нечего, а лишние запросы разряжают телефон. При
 * возврате на экран сессия проверяется сразу, не дожидаясь следующего тика,
 * потому что за время в фоне она могла истечь.
 *
 * Presence на сервере очищается через 90 секунд, поэтому интервал заметно
 * короче: одна пропущенная попытка не должна ронять сессию.
 */
const INTERVAL_MS = 25_000;

export function useSessionHeartbeat() {
  const phase = useAuthStore((state) => state.phase);
  const ping = useAuthStore((state) => state.ping);
  const authenticate = useAuthStore((state) => state.authenticate);

  // Ссылка на актуальный обработчик: интервал пересоздавать при каждой смене не нужно
  const runRef = useRef<() => void>(() => undefined);

  const isActive = isBackendConfigured && (phase === 'authenticated' || phase === 'registration');

  useEffect(() => {
    runRef.current = async () => {
      if (document.visibilityState !== 'visible') return;

      const alive = await ping();
      // Сессия потеряна: по инструкции опрос прекращается и выполняется вход заново
      if (!alive) await authenticate();
    };
  }, [ping, authenticate]);

  useEffect(() => {
    if (!isActive) return undefined;

    const tick = () => void runRef.current();
    const timer = window.setInterval(tick, INTERVAL_MS);

    const onVisibilityChange = () => {
      if (document.visibilityState === 'visible') tick();
    };
    document.addEventListener('visibilitychange', onVisibilityChange);

    return () => {
      window.clearInterval(timer);
      document.removeEventListener('visibilitychange', onVisibilityChange);
    };
  }, [isActive]);
}
